import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { DeviceTimePatternCard } from '../../../components/deviceTimePattern/DeviceTimePatternCard';
import { DeviceTimePatternDetails } from '../../../components/deviceTimePattern/DeviceTimePatternDetails';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { toast } from "react-toastify";
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { DeviceTimePattern as DeviceTimePatternModel } from "../../../models/DeviceTimePattern";
import { TabSummary } from '../../../models/TabSummary';
import { assignDeviceTimePattern, getDeviceTimePatternById, getDeviceTimePatternNamesWithPagination, storeDeviceTimePattern } from '../../../services/deviceTimePatternService';
import { calculateDuration, getTime, setTime } from '../../../services/utilities/dateFormatting';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from "../../../store/types";
import { hideColumns } from '../../../models/utilities/columnHelper';
import TimeTriggeredActionColumn from '../../../models/ColumnDefinitions/TimeTriggeredActionColumn';
import { getTimeTriggeredActions } from '../../../services/timeTriggeredActionService';
import { appendFilter } from '../../../utilities/filters';
import { Lookup } from '../../../models/Lookup';
import AssignWizard from '../../../components/assignWizard/AssignWizard';
import AssignTimeTriggeredAction from '../../../components/deviceTimePattern/assignTimeTriggeredAction';
import LookupTypes from '../../../models/LookupTypes';
import { getAccessTimePatternNamesWithPagination } from '../../../services/accessTimePatternService';
import AccessTimePatternSearch from '../../../models/search/AccessTimePatternSearch';
import SiteSearch from '../../../models/search/SiteSearch';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import DeviceTimePatternSearch from '../../../models/search/DeviceTimePatternSearch';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import TimeTriggeredActionSearch from '../../../models/search/TimeTriggeredActionSearch';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem: DeviceTimePatternModel = {
    days: '',
    id: 0,
    startTime: '',
    endTime: '',
    site: '',
    name: '',
    duration: '',
    dayList: new Array(0),
    default: undefined,
    siteId: undefined,
    startDate: undefined,
    endDate: undefined,
    endTimeExtract: '',
    originalName: ''
}

const allItemsPath = '/access-control/hardware/device-time-patterns';
const itemPath = '/access-control/hardware/device-time-pattern/';
const timeTriggeredActionName = "taName";

const DeviceTimePattern: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.timeTriggeredAction), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<DeviceTimePatternModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const dialog = useDialog();
    const [showAddModal, setShowAddModal] = useState(false);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const getAssignedTimeTriggeredAction = (listParameters) => {
        return getTimeTriggeredActions({
            ...listParameters,
            filters: appendFilter(
                itemId,
                "Time Pattern",
                "timePattern.id",
                listParameters?.filters ?? []
            ),
        });
    };

    const newTimeTriggeredActionColumns = TimeTriggeredActionColumn.map((column) => {
        return {
            ...column,
            active: column.accessor === timeTriggeredActionName ? false : column.active,
        };
    });

    const newTimeTriggeredActionSearch = TimeTriggeredActionSearch.filter((column) => {
        return column.accessor !== timeTriggeredActionName;
    });

    const compileLookup = () => {
        const newLookup = [
            {
                name: 'devAction.deviceAction.deviceImprox.site.id',
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.timePattern,
                values: [],
                dataMethod: getDeviceTimePatternNamesWithPagination,
                searchAccessor: DeviceTimePatternSearch.find(
                    (item) => item.accessor === "name"
                )?.searchOnName,
            },
        ];
        return newLookup;
    };

    useEffect(() => {
        setLookup(compileLookup());
    }, [])


    const topic: Topic = {
        accessor: 'tpName',
        title: translate.byKey(TranslationKeys.deviceTimePatterns),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const getTabContent = (index?: number) => {
        const selectedTabContent = tabContentList[index ?? 0];
        return selectedTabContent.content;
    };

    const tabContentList: ContentWrapper[] = [{
        content: <DeviceTimePatternDetails />
    },
    {
        content: <AssignTimeTriggeredAction
            column={newTimeTriggeredActionColumns}
            searchColumn={newTimeTriggeredActionSearch}
            id={itemId}
            dataMethod={getAssignedTimeTriggeredAction}
            lookup={lookup}>
            <button
                type="button"
                className="flex ml-8 px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                onClick={() => {
                    setShowAddModal(true);
                }}
            >
                {translate.byKey(TranslationKeys.manage)}
            </button>
        </AssignTimeTriggeredAction>
    },]

    const reloadPage = async () => {
        try {
            setTabs([]);
            let deviceTimePattern;

            if (itemId) {
                deviceTimePattern = await getDeviceTimePatternById(itemId, translate);

                if (deviceTimePattern.startTime) {
                    deviceTimePattern.startTime = setTime(deviceTimePattern.startTime);
                    deviceTimePattern.endTime = setTime(deviceTimePattern.endTime);
                }

                setSelectedItem({label: deviceTimePattern.name, value: deviceTimePattern.id.toString(), isDefault: deviceTimePattern.isDefault})
                setCurrentItem(deviceTimePattern);
                const updatedTabs = updateTabBadgeTotal(1, deviceTimePattern?.timeTriggeredActions, initialTabs);
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.deviceTimePatterns) },
                { value: itemPath + `${itemId}`, displayName: `${deviceTimePattern?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        getIds();
    }, [showAddModal])


    const getIds = async () => {
        try {
            const timeTriggeredActionsForTimePattern = (await getTimeTriggeredActions({ filters: itemId ? appendFilter(itemId, 'Time Pattern', 'timePattern.id', []) : [] })).rows;

            if (timeTriggeredActionsForTimePattern) {
                setSelectedIds(timeTriggeredActionsForTimePattern.map(triggeredActionsForTimePattern => triggeredActionsForTimePattern.id.toString()));
                setOldSelectedIds(timeTriggeredActionsForTimePattern.map(triggeredActionsForTimePattern => triggeredActionsForTimePattern.id.toString()));
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const saveDeviceTimePattern = (values) => {
        const deviceTimePattern: DeviceTimePatternModel = {
            ...currentItem,
            name: values.name,
            siteId: itemId ? values.siteId : siteId,
            id: +itemId,
            startTime: values.startTime ? getTime(values.startTime) : "0",
            duration: values.startTime && values.endTime ? calculateDuration(values.startTime, values.endTime) : "2400",
            endTimeExtract: values.endTime ? getTime(values.endTime) : "0",
            startDate: values.startDate,
            endDate: values.endDate,
            dayList: values.dayList
        }

        const isNew = itemId ? +itemId < 0 : true;
        const storeMethod = storeDeviceTimePattern;
        saveItem(setBusy, storeMethod, deviceTimePattern, isNew, setItemId, history);
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const assignTimeTriggeredActions = () => {
        setBusy(true);
        const newSelectedIds = selectedIds.filter(value => value !== "")
        setOldSelectedIds(newSelectedIds);

        const toBeAdded = newSelectedIds.filter((value) => {
            if (!!!oldSelectedIds.includes(value)) {
                return value;
            }
        });

        assignDeviceTimePattern(toBeAdded, itemId)
            .then(async () => {
                const updatedTabs = updateTabBadgeTotal(1, newSelectedIds.length, initialTabs);
                setTabs(updatedTabs);
            }).catch((err) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err) });
            })
            .finally(() => {
                setBusy(false);
            });
    }
    const saveItem = (setBusy, storeMethod, item, createNewItem, setItemId, history) => {
        setBusy(true);
        storeMethod(item, createNewItem)
            .then(async (value) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                reloadPage();
                toast.success(translate.byKey(TranslationKeys.deviceTimePatternSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <div className='flex h-full'>
        <TopicContextProvider topic={topic} dataMethod={getDeviceTimePatternNamesWithPagination} itemId={itemId} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveDeviceTimePattern}
                    tabs={tabs}
                    initialValues={currentItem}
                    itemPath={itemPath}
                    children={
                        <DeviceTimePatternCard />} />
            </ItemContentTabTwo>
        </TopicContextProvider>
        <AssignWizard
            isOpen={showAddModal}
            dataMethod={(listParameters) => getTimeTriggeredActions({ ...listParameters, filters: appendFilter(currentItem?.siteId, 'Site', 'timePattern.site.id', listParameters?.filters ?? []) })}
            lookup={lookup}
            onClose={() => { setShowAddModal(false) }}
            title={translate.byKey(TranslationKeys.assignTimeTriggeredActions)}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            alwaysSelectedAndDisabled={oldSelectedIds}
            columnDescriptors={
                {
                    columns: hideColumns(TimeTriggeredActionColumn, ['timePattern']),
                    searchColumns: TimeTriggeredActionColumn
                }
            }
            handleOnSubmit={assignTimeTriggeredActions} />
    </div>
}

export default DeviceTimePattern;
